import { useEffect, useMemo, useState } from "react";
import { Button } from "risa-oasis-ui_v2";
import CustomTable from "../../../../../../components/custom-table/custom-table";
import { LoaderMessage } from "../../../../../../components/loaderMessage/loaderMessage";
import ConditionListTopValuesModal from "../../../../../../components/modals/ConditionListTopValuesModal/ConditionListTopValuesModal";
import { CmmInputUniqueValuesTableDataModel } from "../../../../../../data-model/tablesData";
import { ModalId } from "../../../../../../enums/modalId";
import { CmmInputUniqueValuesTableColumnKeys } from "../../../../../../enums/tableColumnKeys";
import { CmmInputUniqueValuesTableHeader } from "./conditionListTableData";
import { useConditionList } from "./hooks/useConditionList";
import { useQueryBuilderUsedFields } from "./hooks/useQueryBuilderUsedFields";

function filterConditionListBySearch(
  data: CmmInputUniqueValuesTableDataModel[],
  searchTerm: string,
): CmmInputUniqueValuesTableDataModel[] {
  if (!searchTerm) return data;
  const term = searchTerm.toLowerCase();
  return data.filter((row) => {
    const columnName =
      (row[CmmInputUniqueValuesTableColumnKeys.COLUMN_NAME] as string) ?? "";
    return columnName.toLowerCase().includes(term);
  });
}

const ConditionList = () => {
  const [saving, setSaving] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [workingTableData, setWorkingTableData] = useState<
    ReturnType<typeof useConditionList>["cmmInputUniqueValuesTableData"]
  >([]);
  const {
    cmmInputUniqueValuesTableData,
    loading,
    error,
    refreshing,
    openedModalId,
    metaData,
    handleSaveTopValues,
    handleEditIconClick,
    handleCloseModal,
    handleRefresh,
    handleShowOnListChange,
    handleFieldNameChange,
    handleSave,
  } = useConditionList();

  const { usedFields } = useQueryBuilderUsedFields();

  useEffect(() => {
    if (!isDirty) {
      setWorkingTableData(cmmInputUniqueValuesTableData);
    }
  }, [cmmInputUniqueValuesTableData, isDirty]);

  const filteredTableData = useMemo(
    () => filterConditionListBySearch(workingTableData, searchTerm),
    [workingTableData, searchTerm],
  );

  /** Display data: overlays disabled=true on toggle cells for fields that are
   *  actively referenced in a QueryBuilder rule so they cannot be turned off. */
  const displayTableData = useMemo<CmmInputUniqueValuesTableDataModel[]>(
    () =>
      filteredTableData.map((row) => {
        const columnName = row[
          CmmInputUniqueValuesTableColumnKeys.COLUMN_NAME
        ] as string;
        if (!usedFields.has(columnName)) return row;
        const showOnListCell =
          row[CmmInputUniqueValuesTableColumnKeys.SHOW_ON_LIST];
        return {
          ...row,
          [CmmInputUniqueValuesTableColumnKeys.SHOW_ON_LIST]: {
            ...showOnListCell,
            disabled: true,
          },
        };
      }),
    [filteredTableData, usedFields],
  );

  /** Wraps the original handler to prevent turning off a field that is used in
   *  a QueryBuilder rule. */
  const handleShowOnListChangeGuarded = (data: {
    name: string;
    required: boolean;
    value: boolean;
  }) => {
    const SHOW_ON_LIST_PREFIX = "cmm-show-on-list-";
    if (
      !data.value &&
      typeof data.name === "string" &&
      data.name.startsWith(SHOW_ON_LIST_PREFIX)
    ) {
      const columnName = data.name.slice(SHOW_ON_LIST_PREFIX.length);
      if (usedFields.has(columnName)) return;
    }
    handleShowOnListChange(data, setWorkingTableData, setIsDirty);
  };

  if (loading) {
    return (
      <div className="condition-list__loading flex h-full min-h-0 flex-col">
        <LoaderMessage message="Loading CMM input unique values..." />
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="condition-list-layout flex min-h-0 flex-1 flex-col overflow-hidden">
        <div className="condition-list-layout__header flex flex-shrink-0 flex-col border-b border-primaryGray-15 py-4">
          <div className="font-bold">Condition List</div>
        </div>

        <div className="condition-list-layout__content mb-1 mt-6 flex min-h-0 flex-1 flex-col overflow-y-auto">
          {error && <div className="text-small text-red-600">{error}</div>}
          {workingTableData != null && (
            <>
              <CustomTable
                tableHeaders={CmmInputUniqueValuesTableHeader}
                tableData={displayTableData}
                tableName="cmm-input-unique-values"
                itemsPerPage={25}
                pagesPerView={4}
                count={displayTableData.length}
                hideSearchBar={false}
                hidePagination={false}
                isFetching={refreshing}
                showRefreshButton={true}
                onRefreshButton={handleRefresh}
                onEditIconClick={handleEditIconClick}
                searchingText={(text: string) => setSearchTerm(text)}
                placeholder="Search by Column Name..."
                onTableRowUpdate={(rowIndex, fieldKey, newValue) =>
                  handleFieldNameChange(
                    rowIndex,
                    fieldKey,
                    newValue,
                    setWorkingTableData,
                    setIsDirty,
                  )
                }
                onChangeEmit={handleShowOnListChangeGuarded}
              />
              {openedModalId === ModalId.CONDITION_LIST_TOP_VALUES_MODAL &&
                metaData?.columnName != null && (
                  <ConditionListTopValuesModal
                    onClose={handleCloseModal}
                    onSave={handleSaveTopValues}
                    columnName={metaData.columnName}
                    dataType={metaData.dataType ?? ""}
                    topValues={metaData.topValues ?? []}
                  />
                )}
            </>
          )}
        </div>
        <div className="condition-list-layout__footer flex flex-shrink-0 flex-row justify-end gap-2 border-t border-primaryGray-15 py-4">
          <Button
            buttonType="secondary"
            size="medium"
            onClick={() => {
              setWorkingTableData(cmmInputUniqueValuesTableData);
              setIsDirty(false);
            }}
            disabled={!isDirty}
            children="Cancel"
          />
          <Button
            buttonType="primary"
            size="medium"
            onClick={() => handleSave(workingTableData, setSaving, setIsDirty)}
            disabled={!isDirty || saving}
            children="Save"
          />
        </div>
      </div>
    </div>
  );
};

export default ConditionList;
